import { useRef } from "react";
import { useLocalObservable } from "mobx-react-lite";
import { AsyncStateStore } from "@/utils/AsyncStateStore";
import { useStore } from "./useStore";

/**
 * useAsyncStateStore - Custom React Hook for managing async state with MobX.
 *
 * @param loader - The async function to fetch data.
 * @param options - Optional configuration for the store.
 * @param autoFetchArgs - Optional arguments to auto-fetch data on mount.
 * @returns The MobX store instance.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
export function useAsyncStateStore<
	LoaderFn extends (...args: any[]) => Promise<any>,
	Data = Awaited<ReturnType<LoaderFn>>,
	Args extends any[] = Parameters<LoaderFn>,
	ErrorType = ApiError,
>(loader: LoaderFn, options?: Partial<{ cacheKey: string; debounce: number }>, dismissCriticalError = true) {
	// Create the store instance
	const storeRef = useRef<AsyncStateStore<LoaderFn, Data, Args, ErrorType> | null>(null);

	const {
		uiStore: { modalStore },
	} = useStore();
	const catchCriticalErrors = (error: ErrorType) => {
		console.error("Critical error caught:", error);
		const genericError = error as unknown as ApiError;
		if (!dismissCriticalError && genericError?.status === 500) {
			const description =
				genericError?.message || "An unexpected error occurred. Please try again or contact support.";
			modalStore.open("error", {
				title: "Something went wrong",
				description,
				showRefresh: true,
			});
		} else {
			throw error;
		}
	};

	if (!storeRef.current) {
		storeRef.current = new AsyncStateStore(loader, options, catchCriticalErrors);
	}

	// Wrap with MobX's `useLocalObservable`
	const store = useLocalObservable(() => storeRef.current!);
	return store;
}
